import { TWO_PI } from "../math/constants";
import { Attr, IndexedMesh, TypedArray } from "./indexed-mesh";
import { IndexedMeshWriter } from "./indexed-mesh-writer";

type HoleArrayAttrs = {
    normal: Attr,
    uv: Attr,
}

export class HoleArray extends IndexedMeshWriter<HoleArrayAttrs> {

    x: number;
    y: number;
    width: number;
    height: number;
    cx: number;
    heights: number[];
    radius: number;
    n: number;

    /**
     * @param x - the position of the lower left corner of the array;
     * @param y - the position of the lower left corner of the array; this
     * should be in the same coordinate frame as the heights array
     * @param height - the total height of the array
     * @param cx - the X coordinate of the center of the hole. This should be
     * between x+radius and x+width-radius, otherwise it will be clamped to
     * that interval.
     * @param heights - the height of each hole in global coordinates. Any
     * holes outside of the (elevation, elevation+height) range will be
     * omitted, so this can just be the standard heights array for the
     * customer. This should be sorted from smallest to largest.
     * @param radius - passed to the underlying HoleMesh instances
     * @param n - the number of vertices around the perimeter of the hole. This
     * should be 3 or greater since 2 vertecies form a line, not a polygon.
     */
    constructor(x: number, y: number, width: number, height: number,
                cx: number, heights: number[], radius: number, n: number)
    {
        super();
        this.x = x;
        this.y = y;
        this.width = width;
        this.height = height;
        this.cx = Math.max(x+radius, Math.min(x+width+radius, cx));

        if(heights.length > 0) {
            // The upper and lower limits of holes that fall inside the array,
            // assuming our x coordinate is in the same global frame that the
            // heights are expressed in.
            const
                bottom = y + radius,
                top = y + height - radius,
                diameter = 2 * radius;
            let lastHeight = -Infinity;
            this.heights = heights.filter(h => {
                const distance = h - lastHeight;
                lastHeight = h;
                return h > bottom && h < top && distance >= diameter
            });
        }

        this.radius = radius;
        this.n = Math.max(3, n);
    }

    computeAttributeCounts(): IndexedMesh<HoleArrayAttrs> {
        const
            mesh = new IndexedMesh({
                normal: new Attr(3),
                uv: new Attr(2),
            }),
            heights = this.heights,
            n = this.n,
            // m is the number of holes
            m = heights.length,
            // At least four corners, add another two for every hole
            cornerVertexes = m == 0 ? 4 : 2*(m + 1),
            holeVertexes = m * n,
            vertexes = cornerVertexes + holeVertexes,
            // There is a triangle covering each side of the enclosing quad and
            // touching only one vertex of the hole. Then there is an edge
            // following each vertex that forms a triangle with one of the
            // corners. If there are no holes, then we just have a quad, i.e.
            // two triangles.
            triangles = m == 0 ? 2 : 4*m + holeVertexes;

        mesh.index.count = triangles * 3;
        mesh.position.count = vertexes;
        mesh.attrs.normal.count = vertexes;
        mesh.attrs.uv.count = vertexes;

        return mesh;
    }

    computeMeshData(mesh: IndexedMesh<HoleArrayAttrs>): void {
        const
            x = this.x,
            y = this.y,
            width = this.width,
            height = this.height,
            // The x-coordintate of the center of each hole
            cx = this.cx, //x+width/2,
            heights = this.heights,
            r = this.radius,
            n = this.n,
            m = this.heights.length,
            idx = mesh.index.data,
            pos = mesh.position.data,
            norm = mesh.attrs.normal.data,
            uv = mesh.attrs.uv.data,

            // the x-coordinate of the right side of the rectangle
            right = x+width,
            // the y-coordinate of the top of the rectangle
            top = y+height;

        let
            // p is our current data index for pos, it advances by threes
            p = 0,
            // u is our current data index uv, it advances by twos
            u = 0;

        // Create all the corner vertexes for the enclosing quads. The left
        // column followed by the right column, such that the first element 
        // index, 0, is the lower left corner, element index n+1 is the lower
        // right corner. This fixed and predictable structure is how we keep
        // track of the corners as we're adding indexes; note how the structure
        // parallels our order of iteration when we form holes from
        // bottom-to-top.

        // If we have no holes, we're just a quad
        if(m == 0) {
            fourCorners(x, y, width, height, pos, uv);

            idx[0] = 0; idx[1] = 2; idx[2] = 3;
            idx[3] = 0; idx[4] = 3; idx[5] = 1;

            // we only write the corners, so no need to update p or u
        }
        else if(m == 1) {
            fourCorners(x, y, width, height, pos, uv);
            // 4 corners * x, y, z positions = 12 data points in pos
            // 4 corners * x, y positions = 8 data points in uv
            p = 12; u = 8;
        } else {
            // The element indices of the left column run from 0-m, and the
            // right column runs from m+1 to 2*m+1.

            let
                pl = 0, pr = 3*(m+1),
                ul = 0, ur = 2*(m+1);

            // bottom-left vertex
            pos[pl++] = x;       pos[pl++] = y; pl++; // omit the z = 0
             uv[ul++] = x;        uv[ul++] = y;
            // bottom-right vertex
            pos[pr++] = right; pos[pr++] = y; pr++; // omit the z = 0
             uv[ur++] = right;  uv[ur++] = y;

            // every vertex between two heights
            for(let h = 1; h < m; h++) {
                const midpoint = (heights[h-1] + heights[h]) / 2;
                pos[pl++] = x;       pos[pl++] = midpoint; pl++; // omit z = 0
                 uv[ul++] = x;        uv[ul++] = midpoint;

                pos[pr++] = right;   pos[pr++] = midpoint; pr++; // omit z = 0
                 uv[ur++] = right;    uv[ur++] = midpoint;
            }

            // top two vertexes
            pos[pl++] = x;     pos[pl++] = top; // omit z = 0
             uv[ul++] = x;      uv[ul++] = top;
            pos[pr++] = right; pos[pr++] = top; pr++// omit z = 0
             uv[ur++] = right;  uv[ur++] = top;

            //Copy the next data index into p and u
            p = pr; u = ur;
        }

        // Next we create all the hole perimeter vertexes and connect them to
        // the corresponding corner points.

        // Track the lower-left, lower-right, upper-left, and upper-right
        // element indices.
        let ll = 0, lr = m+1,
            ul = 1, ur = lr+1,

            // v0 is the first element index where we can write a hole,
            // which is right after the vertices for the outer quads.
            // Note that v0 is a reference to the Nth element, i.e. vertex or
            // uv point, so it advances by n below because we are adding n
            // elements (n*3 floats) per hole.
            v0 = 2*(m+1),

            // i is our current marker of where we are writing into idx. This
            // advances three places for every triangle we write into the
            // index. Since there are n+4 triangles for every quad+hole unit
            // in the array, this advances by (n+4)*3 for each unit.
            i = 0;

        // Note that this won't run if we have 0 holes
        for(const cy of heights) {
            // adds positions and uvs for a hole that has n vertexes.
            hole(cx, cy, r, n, pos, p, uv, u);
            // Note that we could just run this for heights[0] once and then
            // copy all the pos and uv values forward by n*3 and n*2 data
            // indices.

            cellIdx(ll, lr, ul, ur, v0, n, idx, i);

            // advance markers one hole
            p += n*3;
            u += n*2;
            v0 += n;
            i += (n+4)*3;

            // advance the corner indices up one slot
            ll++; lr++;
            ul++; ur++;
        }

        // Since the face normal is the same everywhere, we can simply set it
        // all in one go.
        for(let j = 0; j < norm.length; j+=3) {
            norm[j] = 0; norm[j+1] = 0; norm[j+2] = 1;
        }
    }
}

/**
 * Write the four corner vertexes of the degenerate cases where we have 0 or 1
 * holes. We always write these to the start of the pos/uv arrays so they have
 * the lowest index possible.
 */
const fourCorners = (x: number, y: number, width: number, height: number,
                 pos: TypedArray, uv: TypedArray) =>
{
    const x1 = x+width, y1 = y+height;
    pos[0] = x;  pos[1]  = y;   // skip z
    pos[3] = x;  pos[4]  = y1;
    pos[6] = x1; pos[7]  = y;
    pos[9] = x1; pos[10] = y1;

    uv[0] = x;  uv[1] = y;
    uv[2] = x;  uv[3] = y1;
    uv[4] = x1; uv[5] = y;
    uv[6] = x1; uv[7] = y1;
}

/**
 * Writes n vertices counterclockwise, starting from the x-axis at one radius
 * to the right of the origin (x, y). You should advance your pointer into
 * pos by n*3 for each call, and the same for uv by n*2.
 * 
 * @param x - the center of the hole
 * @param y - the center of the hole
 * @param r - radius of the hole
 * @param n - the number of vertices
 * @param pos - should be a sub-array aligned to the correct offset
 * @param p - index of first hole vertex in pos
 * @param uv - should be a sub-array aligned to the correct offset
 * @param u - index of first uv point in the uv array
 */
const hole = (x: number, y: number, r: number, n: number,
              pos: TypedArray, p: number, uv: TypedArray, u: number) =>
{
    const inc = TWO_PI / n;

    pos[p++] = x+r; pos[p++] = y; p++; // pos[p] = 0;
     uv[u++] = x+r;  uv[u++] = y;

    for(let i = 1; i < n; i++) {
        const
            t = i*inc,
            vx = x + r*Math.cos(t),
            vy = y + r*Math.sin(t);

        pos[p++] = vx; pos[p++] = vy; p++; // pos[p] = 0;
         uv[u++] = vx;  uv[u++] = vy;
    }
}

/**
 * Computes the triangle connections for a single cell of the array. This is
 * purely a topological problem so none of the measurements are required.
 *
 * @param ll - lower-left corner vertex's index
 * @param lr - lower-right corner vertex's index
 * @param ul - upper-left corner vertex's index
 * @param ur - upper-right corner vertex's index
 * @param v0 - the element index of the first element of the hole
 * @param n - how many hole vertices in the array
 * @param i - where to start adding triangles in the index array
 */
const cellIdx = (ll: number, lr: number, ul: number, ur: number,
                 v0: number, n: number, idx: TypedArray, i: number) =>
{
    // the triangle consisting of the first vertex of the hole and the right
    // edge of the enclosing quad.
    idx[i++] = v0; idx[i++] = lr; idx[i++] = ur;

    let j=0;

    // Group one connects to the upper right corner
    const g1 = Math.trunc(0.25*n);
    while(j < g1) {
        idx[i++] = v0 + j; idx[i++] = ur; idx[i++] = v0 + j + 1;
        j++;
    }

    // triangle consisting of the top edge and the vertex at the end of g1
    idx[i++] = ur; idx[i++] = ul; idx[i++] = v0 + j;

    // Group two connects to the upper left corner
    const g2 = Math.trunc(0.5*n);
    while(j < g2) {
        idx[i++] = v0 + j; idx[i++] = ul; idx[i++] = v0 + j + 1;
        j++;
    }

    // triangle consisting of the left edge and the vertex at the end of g2
    idx[i++] = ul; idx[i++] = ll; idx[i++] = v0 + j;

    // Group three connects to the lower left corner
    const g3 = Math.trunc(0.75*n);
    while(j < g3) {
        idx[i++] = v0 + j; idx[i++] = ll; idx[i++] = v0 + j + 1;
        j++;
    }

    // triangle consisting of the bottom edge and the vertex at the end of g3
    idx[i++] = ll; idx[i++] = lr; idx[i++] = v0 + j;

    // Group four connects to the lower right corner. Note that we skip the
    // last side of the hole and do that manually at the very end since it
    // connects back to the first vertex.
    const g4 = n - 1;
    while(j < g4) {
        idx[i++] = v0 + j; idx[i++] = lr; idx[i++] = v0 + j + 1;
        j++;
    }

    // triangle consisting of the last edge of the hole and the lower right
    // corner of the enclosing quad
    idx[i++] = v0; idx[i++] = v0+n-1; idx[i++] = lr;
}
